<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col pt-3">
      <div class="relative z-30 px-6 pb-0 flex flex-col gap-4 border-b border-slate-100 bg-white max-md:overflow-visible">
        <!-- <h2 class="text-base font-bold text-[#0d4169] text-left m-0">Finance Report</h2> -->
        <div
          v-if="visibleReportDropdownItems.length > 0"
          ref="tabsBarRef"
          class="finance-tabs-bar relative mt-0 pt-0 -mb-[1px] max-md:-mx-6 max-md:px-6"
        >
          <div class="finance-tabs-scroll flex justify-start gap-4 md:gap-6 max-md:overflow-x-auto max-md:flex-nowrap">
            <div class="relative max-md:shrink-0">
              <button
                type="button"
                @click="toggleReportsDropdown"
                :class="{
                  'border-[#0d4169] text-[#0d4169]': isReportsTabActive,
                  'border-transparent text-slate-500': !isReportsTabActive
                }"
                class="pb-2 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors inline-flex items-left gap-1 max-md:whitespace-nowrap max-md:shrink-0"
              >
                <span class="max-md:whitespace-nowrap">Finance Reports</span>
                <svg
                  class="mt-1 w-3 h-3 shrink-0 transition-transform duration-200"
                  :class="showReportsDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <div
                v-if="showReportsDropdown"
                class="hidden md:block absolute left-0 top-full mt-2 w-56 rounded-md border border-slate-200 bg-white shadow-lg z-50"
              >
                <button
                  v-for="item in visibleReportDropdownItems"
                  :key="item.key"
                  type="button"
                  @click="onReportItemClick(item)"
                  class="w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-blue-600 hover:text-white border-b border-slate-100 last:border-b-0 transition-colors"
                >
                  {{ item.label }}
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="showReportsDropdown"
            class="finance-tabs-mobile-dropdown md:hidden absolute left-0 right-0 top-full z-[60] flex justify-start px-4 pt-2"
          >
            <div class="w-56 max-w-[calc(100vw-3rem)] rounded-md border border-slate-200 bg-white shadow-lg">
              <button
                v-for="item in visibleReportDropdownItems"
                :key="`mobile-${item.key}`"
                type="button"
                @click="onReportItemClick(item)"
                class="w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-blue-600 hover:text-white border-b border-slate-100 last:border-b-0 transition-colors"
              >
                {{ item.label }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="relative z-0 px-6 py-4">
        <ProfitAndLoss v-if="activeReportView === 'profit_and_loss'" />
        <VatPage v-else-if="activeReportView === 'vat'" />
        <PurchaseReport v-else-if="activeReportView === 'purchase_report'" />
        <SalesDeclaration v-else-if="activeReportView === 'sales_report'" />
        <PayrollReport v-else-if="activeReportView === 'payroll_report'" />
        <BankReconciliation v-else-if="activeReportView === 'bank_reconciliation_report'" />
        <ReceivablesPage v-else-if="activeReportView === 'receivables'" />
        <LiabilityPage v-else-if="activeReportView === 'liability'" />

        <div v-else class="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
          <h3 class="text-sm font-bold text-slate-700 mb-2">
            {{ contentPlaceholderHeading }}
          </h3>
          <p class="text-xs text-slate-500">
            {{ activeReportView ? 'Report content will appear here.' : 'Select a report from the list' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ProfitAndLoss from './profitAndLoss.vue'
import VatPage from './vatPage.vue'
import PurchaseReport from './purchaseReport.vue'
import SalesDeclaration from './salesDeclaration.vue'
import PayrollReport from './payrollReport.vue'
import BankReconciliation from './bankReconciliationReport.vue'
import ReceivablesPage from './receivablesPage.vue'
import LiabilityPage from './liabilityPage.vue'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAdminPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeBlock(name) {
  const privs = userPrivileges.value || {}
  const exact = privs?.[name]
  if (exact) return exact
  const lower = privs?.[String(name).toLowerCase()]
  return lower || null
}

function hasReportViewPrivilege(privilegeName) {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock(privilegeName)?.View
}

const allReportDropdownItems = [
  { key: 'profit_and_loss', label: 'Profit And Loss', privilege: 'Profit and Loss' },
  { key: 'vat', label: 'V.A.T', privilege: 'V.A.T' },
  { key: 'purchase_report', label: 'Purchase Declaration', privilege: 'Purchase Declaration' },
  { key: 'sales_report', label: 'Sales Declaration', privilege: 'Sales Declaration' },
  { key: 'payroll_report', label: 'Payroll Report', privilege: 'Payroll Report' },
  { key: 'bank_reconciliation_report', label: 'Bank Reconciliation Report', privilege: 'Bank Reconciliation Report' },
  { key: 'receivables', label: 'Receivables', privilege: 'Receivables' },
  { key: 'liability', label: 'Liability', privilege: 'Liability' }
]

const visibleReportDropdownItems = computed(() => {
  if (hasAdminPrivilege.value) return allReportDropdownItems
  return allReportDropdownItems.filter((item) => hasReportViewPrivilege(item.privilege))
})

const activeReportView = ref(null)
const showReportsDropdown = ref(false)
const tabsBarRef = ref(null)

watch(
  visibleReportDropdownItems,
  (items) => {
    if (!items?.length) {
      activeReportView.value = null
      return
    }
    // if (!items.some((item) => item.key === activeReportView.value)) {
    //   // activeReportView.value = items[0].key
    // }
  },
  { immediate: true }
)

const isReportsTabActive = computed(
  () => showReportsDropdown.value || activeReportView.value != null
)

const contentPlaceholderHeading = computed(() => {
  if (activeReportView.value) {
    const item = allReportDropdownItems.find((i) => i.key === activeReportView.value)
    return item?.label || 'Finance Reports'
  }
  return 'Finance Reports'
})

function closeReportsDropdown() {
  showReportsDropdown.value = false
}

function toggleReportsDropdown() {
  showReportsDropdown.value = !showReportsDropdown.value
}

function onReportItemClick(item) {
  if (item?.key) {
    activeReportView.value = item.key
  }
  closeReportsDropdown()
}

function handleGlobalPointerDown(e) {
  const el = tabsBarRef.value
  if (!el) return
  if (el.contains(e.target)) return
  closeReportsDropdown()
}

onMounted(() => {
  document.addEventListener('pointerdown', handleGlobalPointerDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleGlobalPointerDown)
})
</script>

<style scoped>
@media (max-width: 767px) {
  .finance-tabs-scroll {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .finance-tabs-scroll::-webkit-scrollbar {
    height: 4px;
  }

  .finance-tabs-scroll::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 4px;
  }
}
</style>
